/**
 * TemplateEditorModal — Phase 2.4
 *
 * Full-screen template editor matching prototype-101-template-editor.html:
 *   - Header: template name input + default toggle + Cancel / Save as new / Save
 *   - Left rail: 9 token groups (Visual / Sections / Content)
 *   - Centre form: per-group field editors
 *   - Right pane: live mini-preview (cover band + finding card) that re-skins on change
 *
 * Reads/writes via:
 *   cipherops/templates/update   — Save
 *   cipherops/templates/create   — Save as new
 *
 * Theme JSON structure mirrors latest/templates/citadel-default.json. Missing
 * fields fall back to CITADEL_DEFAULTS so older templates can be edited too.
 */

import { useState, useMemo, useCallback, useEffect, useRef, createContext, useContext } from 'react';
import { invoke, IpcError } from '../lib/ipc';
import { parseJsonField } from '../lib/parse-json-field';
import { ConfirmDestructiveModal } from './ConfirmDestructiveModal';
// The cover preview renders via IPC (cipherops/templates/preview_cover) so
// the renderer runs in the Electron main process — the same path the actual
// report export uses. This avoids the entire class of Vite/Electron
// CJS-bundling stale-cache issues we hit when calling renderCoverPage from
// the browser bundle. See Gconfig/cipherops#12 + CLAUDE.md §13.

// ---------------------------------------------------------------------------
// Default theme (mirrors latest/templates/citadel-default.json)
// ---------------------------------------------------------------------------

export const CITADEL_DEFAULTS = {
  palette: {
    bg: '#f6f4f0', surface: '#ffffff', surface_alt: '#eae8e2',
    brand: '#1a1f2e', accent: '#8b1a2e', text: '#1a1f2e',
    text_muted: '#7a7d8a', border: '#d0ccc4', border_light: '#e0ddd8',
    code_bg: '#f0ede8',
  },
  severity: {
    critical: { border: '#991b1b', text: '#991b1b', label: 'CRITICAL', enabled: true },
    high:   { border: '#c44a1a', text: '#c44a1a', label: 'HIGH', enabled: true },
    medium: { border: '#2d7a42', text: '#1e6635', label: 'MEDIUM', enabled: true },
    low:    { border: '#1a5a8b', text: '#1a5a8b', label: 'LOW', enabled: true },
    info:   { border: '#4a5568', text: '#4a5568', label: 'INFO', enabled: true },
  },
  typography: {
    serif: "Georgia, 'Times New Roman', serif",
    mono: "'Courier New', Courier, monospace",
    sans: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    size_body: '14px', size_heading: '19px', size_section: '10px',
    size_meta: '11px', size_code: '12px', size_cover_title: '30px',
    size_cover_client: '26px', line_height: '1.8',
  },
  cover: {
    layout_id: 'A',
    header_height: '80px',
    // confidentiality_text drives the centred footer-band text on layouts
    // that have one (A, H). The legacy `show_confidentiality` toggle was
    // dropped in B5 — orphaned (cover.ts never read it).
    confidentiality_text: 'CONFIDENTIAL — CLIENT EYES ONLY',
  },
  // Per-layout palette overrides. Keys are layout IDs (A–H). Only layouts
  // with consultant-set overrides appear; missing fields fall through to the
  // global `palette` above. Stored inside theme_json — no DB migration needed.
  cover_overrides: {} as Record<string, { palette: Partial<Record<string, string>> }>,
  findings: {
    severity_border_width: '3px',
    card_radius: '0px',
    show_cvss: true,
  },
  appendix: {
    show_scope: true, show_methodology: true,
    show_tools: true, show_tester_bio: false,
  },
  http: {
    bg: '#1a1f2e', text: '#e2e8f0',
    header_name: '#8bafd4', header_value: '#cbd5e1',
    method_get: '#22c55e', method_post: '#f59e0b',
    method_put: '#3b82f6', method_delete: '#c44a1a', method_options: '#8b5cf6',
    status_2xx: '#22c55e', status_3xx: '#f59e0b',
    status_4xx: '#f97316', status_5xx: '#c44a1a',
    url_path: '#93c5fd', url_param: '#fbbf24', payload_highlight: '#fef08a',
    font_size: '11px', show_line_numbers: true, line_num_color: '#3d4a5c',
  },
};

export type ThemeJson = typeof CITADEL_DEFAULTS;

// ---------------------------------------------------------------------------
// Focus tracking — Phase 2.10
// ---------------------------------------------------------------------------
// When a field input receives focus, it announces its tokenPath via this
// context. The PreviewPane reads the focused token and outlines matching
// elements with a dashed accent border so the consultant can see exactly
// which part of the rendered output their edit affects.
// ---------------------------------------------------------------------------

interface TokenFocusValue {
  focused: string | null;
  setFocused: (path: string | null) => void;
}

export const TokenFocusContext = createContext<TokenFocusValue>({
  focused: null,
  setFocused: () => { /* default no-op for legacy callers */ },
});

/**
 * Compute an outline style if any of the given tokenPaths matches the
 * currently-focused field. Used inside PreviewPane to flag elements
 * affected by the edit-in-progress.
 */
export function focusStyle(focused: string | null, ...tokens: string[]): React.CSSProperties {
  if (!focused) return {};
  return tokens.includes(focused)
    ? {
        outline: '2px dashed var(--accent)',
        outlineOffset: 2,
        transition: 'outline 0.15s, outline-offset 0.15s',
        position: 'relative',
        zIndex: 1,
      }
    : {};
}

// ---------------------------------------------------------------------------
// Rail config
// ---------------------------------------------------------------------------

interface RailGroup {
  id: keyof ThemeJson;
  label: string;
  icon: string;
  section: 'Visual' | 'Sections' | 'Content';
  count: number;
}

const RAIL_GROUPS: RailGroup[] = [
  // Icons intentionally empty — see TemplateEditorScreen.tsx RAIL_ITEMS
  // for the rationale. Field kept on the type so consumers don't break.
  { id: 'palette',    label: 'Palette',            icon: '', section: 'Visual',   count: 10 },
  { id: 'severity',   label: 'Severity',           icon: '', section: 'Visual',   count: 4 },
  { id: 'typography', label: 'Typography',         icon: '', section: 'Visual',   count: 11 },
  { id: 'cover',      label: 'Cover',              icon: '', section: 'Sections', count: 3 },
  { id: 'findings',   label: 'Findings',           icon: '', section: 'Sections', count: 4 },
  { id: 'appendix',   label: 'Appendix',           icon: '', section: 'Sections', count: 4 },
  { id: 'http',       label: 'HTTP Blocks',        icon: '', section: 'Sections', count: 19 },
  // Text Overrides + Section Visibility retired 2026-07-07 — both are now done
  // inline, per-engagement, in the Report Builder (section on/off toggles +
  // inline rename) rather than at the template level.
];

// ---------------------------------------------------------------------------
// Reusable field helpers
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Colour-space helpers — used by the lighten/darken stepper buttons
// ---------------------------------------------------------------------------

function hexToRgb(hex: string): [number, number, number] | null {
  const m = (hex || '').trim().match(/^#?([a-f0-9]{6})$/i);
  if (!m) return null;
  const v = m[1];
  return [parseInt(v.slice(0, 2), 16), parseInt(v.slice(2, 4), 16), parseInt(v.slice(4, 6), 16)];
}

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }
  return [h * 360, s * 100, l * 100];
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  h /= 360; s /= 100; l /= 100;
  if (s === 0) {
    const v = Math.round(l * 255);
    return [v, v, v];
  }
  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const r = hue2rgb(p, q, h / 360 + 1 / 3);
  const g = hue2rgb(p, q, h / 360);
  const b = hue2rgb(p, q, h / 360 - 1 / 3);
  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
}

function rgbToHex(r: number, g: number, b: number): string {
  return '#' + [r, g, b].map(x => Math.max(0, Math.min(255, x)).toString(16).padStart(2, '0')).join('');
}

/** Set the HSL lightness of a hex colour to an absolute value (0-100). */
function setLightness(hex: string, l: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const [h, s] = rgbToHsl(rgb[0], rgb[1], rgb[2]);
  const clamped = Math.max(0, Math.min(100, l));
  const [r, g, b] = hslToRgb(h, s, clamped);
  return rgbToHex(r, g, b);
}

/** Read the current lightness (0-100) of a hex colour. */
function getLightness(hex: string): number {
  const rgb = hexToRgb(hex);
  if (!rgb) return 50;
  return rgbToHsl(rgb[0], rgb[1], rgb[2])[2];
}

/**
 * Walk CITADEL_DEFAULTS by dot-path so each ColourField can offer a
 * "reset to seed default" button without needing the parent to pass
 * the default value as a prop.
 */
function lookupDefault(path: string): string | undefined {
  const parts = path.split('.');
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let cur: any = CITADEL_DEFAULTS;
  for (const p of parts) {
    if (cur && typeof cur === 'object' && p in cur) cur = cur[p];
    else return undefined;
  }
  return typeof cur === 'string' ? cur : undefined;
}

/**
 * Curated swatch palette — stops consultants picking near-duplicate hex
 * values for each token (e.g. #f6f4f0 vs #f6f4f2). Grouped so the picker
 * popover can show meaningful sections instead of one long rainbow.
 *
 * Citadel + Severity rows match the design tokens already used by the
 * default theme so a one-click "snap back" is always available.
 */
const SWATCH_GROUPS: Array<{ name: string; swatches: Array<{ hex: string; name: string }> }> = [
  {
    name: 'Default',
    swatches: [
      { hex: '#1a1f2e', name: 'Brand navy' },
      { hex: '#8b1a2e', name: 'Accent crimson' },
      { hex: '#f6f4f0', name: 'Cream bg' },
      { hex: '#ffffff', name: 'Pure white' },
      { hex: '#eae8e2', name: 'Surface alt' },
      { hex: '#d0ccc4', name: 'Border' },
      { hex: '#e0ddd8', name: 'Border light' },
      { hex: '#f0ede8', name: 'Code bg' },
    ],
  },
  {
    name: 'Severity',
    swatches: [
      { hex: '#c44a1a', name: 'High orange' },
      { hex: '#2d7a42', name: 'Medium green' },
      { hex: '#1a5a8b', name: 'Low blue' },
      { hex: '#4a5568', name: 'Info slate' },
      { hex: '#1e6635', name: 'Medium text' },
    ],
  },
  {
    name: 'Neutrals',
    swatches: [
      { hex: '#000000', name: 'Black' },
      { hex: '#1f2937', name: 'Charcoal' },
      { hex: '#4b5563', name: 'Slate' },
      { hex: '#7a7d8a', name: 'Muted' },
      { hex: '#9ca3af', name: 'Light slate' },
      { hex: '#d1d5db', name: 'Light grey' },
      { hex: '#e5e7eb', name: 'Off-white' },
      { hex: '#f3f4f6', name: 'Near white' },
    ],
  },
  {
    name: 'Rainbow',
    swatches: [
      { hex: '#dc2626', name: 'Red' },
      { hex: '#ea580c', name: 'Orange' },
      { hex: '#d97706', name: 'Amber' },
      { hex: '#ca8a04', name: 'Yellow' },
      { hex: '#65a30d', name: 'Lime' },
      { hex: '#16a34a', name: 'Green' },
      { hex: '#0891b2', name: 'Cyan' },
      { hex: '#2563eb', name: 'Blue' },
      { hex: '#7c3aed', name: 'Violet' },
      { hex: '#db2777', name: 'Pink' },
    ],
  },
];

function ColourField({ label, value, onChange, hint, tokenPath }: {
  label: string; value: string; onChange: (v: string) => void; hint?: string; tokenPath?: string;
}) {
  const [open, setOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const chipRef    = useRef<HTMLButtonElement | null>(null);
  const { setFocused } = useContext(TokenFocusContext);
  const announceFocus = () => tokenPath && setFocused(tokenPath);
  const announceBlur  = () => tokenPath && setFocused(null);
  // Lightness slider state — derived from the current hex value so the
  // slider always reflects what's in the field, even after a swatch click
  // or a manual hex edit.
  const currentL = Math.round(getLightness(value));
  const seedDefault = tokenPath ? lookupDefault(tokenPath) : undefined;
  const showReset = seedDefault !== undefined && seedDefault.toLowerCase() !== (value || '').toLowerCase();

  // Close on outside click or Escape so the popover doesn't sticky-open.
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      const t = e.target as Node;
      if (popoverRef.current?.contains(t)) return;
      if (chipRef.current?.contains(t)) return;
      setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    window.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const isCurrent = (hex: string) => hex.toLowerCase() === (value || '').toLowerCase();

  return (
    <div
      className="tpl-field"
      style={{ position: 'relative' }}
      onMouseEnter={announceFocus}
      onMouseLeave={() => { if (!open) announceBlur(); }}
    >
      <label>{label}</label>
      <div className="tpl-field-row">
        <button
          ref={chipRef}
          type="button"
          onClick={() => setOpen((v) => !v)}
          onFocus={announceFocus}
          onBlur={announceBlur}
          aria-label={`${label} swatch picker`}
          aria-haspopup="dialog"
          aria-expanded={open}
          style={{
            width: 32, height: 32,
            border: '1px solid var(--border)', borderRadius: 3,
            background: value, cursor: 'pointer',
            padding: 0, position: 'relative',
            outline: open ? '2px solid var(--accent)' : 'none',
            outlineOffset: 1,
          }}
          title={`Click to choose from preset swatches · current: ${value}`}
        />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={announceFocus}
          onBlur={announceBlur}
          style={{ flex: 1, padding: '5px 8px', fontFamily: 'var(--mono)', fontSize: 11, border: '1px solid var(--border)', borderRadius: 3, background: 'var(--surface)' }}
        />
      </div>

      {/* Lightness slider — drag to make the colour darker (left) or lighter
          (right). Reset button appears when the current value differs from
          the seed default and snaps back to the default on click. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
        <span title="Darken" style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-muted)', width: 10, textAlign: 'center' }}>◐</span>
        <input
          type="range"
          min={0} max={100} step={1}
          value={currentL}
          onChange={(e) => onChange(setLightness(value, parseInt(e.target.value, 10)))}
          onMouseEnter={announceFocus}
          onMouseLeave={announceBlur}
          onFocus={announceFocus}
          onBlur={announceBlur}
          aria-label={`${label} lightness`}
          title={`Lightness ${currentL}% — drag to darken or lighten`}
          style={{ flex: 1, height: 4, accentColor: 'var(--accent)', cursor: 'ew-resize' }}
        />
        <span title="Lighten" style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-muted)', width: 10, textAlign: 'center' }}>◑</span>
        <span style={{ fontFamily: 'var(--mono)', fontSize: 10.5, color: 'var(--text-muted)', minWidth: 26, textAlign: 'right' }}>
          {currentL}%
        </span>
        {showReset && (
          <button
            type="button"
            onClick={() => onChange(seedDefault!)}
            title={`Reset to seed default · ${seedDefault}`}
            aria-label={`Reset ${label} to default`}
            style={{
              background: 'transparent', border: '1px solid var(--border-light)',
              borderRadius: 3, cursor: 'pointer',
              fontSize: 11, color: 'var(--text-muted)',
              width: 22, height: 18, padding: 0,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            ↻
          </button>
        )}
      </div>

      {hint && <span className="tpl-field-hint">{hint}</span>}

      {open && (
        <div
          ref={popoverRef}
          role="dialog"
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)', left: 0,
            zIndex: 50,
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 4,
            boxShadow: '0 6px 24px rgba(0,0,0,0.16)',
            padding: '10px 12px',
            width: 280,
          }}
        >
          {SWATCH_GROUPS.map((group) => (
            <div key={group.name} style={{ marginBottom: 10 }}>
              <div style={{
                fontFamily: 'var(--mono)', fontSize: 10.5, fontWeight: 700,
                letterSpacing: '0.14em', textTransform: 'uppercase',
                color: 'var(--text-muted)', marginBottom: 5,
              }}>
                {group.name}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 4 }}>
                {group.swatches.map((sw) => (
                  <button
                    key={sw.hex}
                    type="button"
                    onClick={() => { onChange(sw.hex); setOpen(false); }}
                    title={`${sw.name} · ${sw.hex}`}
                    aria-label={`${sw.name} ${sw.hex}`}
                    style={{
                      width: '100%', aspectRatio: '1 / 1',
                      background: sw.hex,
                      border: isCurrent(sw.hex) ? '2px solid var(--accent)' : '1px solid var(--border)',
                      borderRadius: 2, padding: 0,
                      cursor: 'pointer',
                      position: 'relative',
                    }}
                  >
                    {isCurrent(sw.hex) && (
                      <span style={{
                        position: 'absolute', top: -1, right: -1,
                        width: 8, height: 8, borderRadius: '50%',
                        background: 'var(--accent)',
                        border: '1.5px solid var(--surface)',
                      }} />
                    )}
                  </button>
                ))}
              </div>
            </div>
          ))}
          <div style={{
            paddingTop: 8,
            borderTop: '0.5px solid var(--border-light)',
          }}>
            <div style={{
              fontFamily: 'var(--mono)', fontSize: 10.5, fontWeight: 700,
              letterSpacing: '0.14em', textTransform: 'uppercase',
              color: 'var(--text-muted)', marginBottom: 5,
            }}>
              Custom
            </div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input
                type="color"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                style={{ width: 36, height: 28, border: '1px solid var(--border)', borderRadius: 3, padding: 0, cursor: 'pointer', flexShrink: 0 }}
                aria-label={`${label} custom colour picker`}
              />
              <input
                type="text"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder="#rrggbb"
                style={{ flex: 1, padding: '5px 8px', fontFamily: 'var(--mono)', fontSize: 11, border: '1px solid var(--border)', borderRadius: 3, background: 'var(--surface)' }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TextField({ label, value, onChange, hint, mono = false }: {
  label: string; value: string; onChange: (v: string) => void; hint?: string; mono?: boolean;
}) {
  return (
    <div className="tpl-field">
      <label>{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{ padding: '5px 8px', fontFamily: mono ? 'var(--mono)' : 'inherit', fontSize: mono ? 11 : 12, border: '1px solid var(--border)', borderRadius: 3, background: 'var(--surface)' }}
      />
      {hint && <span className="tpl-field-hint">{hint}</span>}
    </div>
  );
}

/**
 * Curated font stacks for each typeface role. The web-safe stacks at the
 * top render without bundling external font files; the Google Font entries
 * lower down assume the consultant has loaded them via firm settings or
 * an upstream stylesheet (CipherOps does not auto-import web fonts).
 */
const SERIF_STACKS = [
  { label: 'Georgia (default)', value: "Georgia, 'Times New Roman', serif" },
  { label: "Times New Roman",            value: "'Times New Roman', Times, serif" },
  { label: 'Cambria',                    value: "Cambria, Georgia, serif" },
  { label: 'Palatino',                   value: "'Palatino Linotype', Palatino, serif" },
  { label: 'Garamond',                   value: "'EB Garamond', Garamond, Georgia, serif" },
  { label: 'Charter',                    value: "Charter, Georgia, serif" },
];
const MONO_STACKS = [
  { label: "Courier New (default)", value: "'Courier New', Courier, monospace" },
  { label: 'Consolas',                       value: "Consolas, 'Courier New', monospace" },
  { label: 'Monaco / Menlo',                 value: "Menlo, Monaco, 'Courier New', monospace" },
  { label: 'SF Mono',                        value: "'SF Mono', Menlo, monospace" },
  { label: 'JetBrains Mono',                 value: "'JetBrains Mono', Menlo, monospace" },
  { label: 'Source Code Pro',                value: "'Source Code Pro', Menlo, monospace" },
];
const SANS_STACKS = [
  { label: 'System UI (default)', value: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" },
  { label: 'Helvetica Neue',              value: "'Helvetica Neue', Helvetica, Arial, sans-serif" },
  { label: 'Arial',                       value: "Arial, Helvetica, sans-serif" },
  { label: 'Segoe UI',                    value: "'Segoe UI', Tahoma, sans-serif" },
  { label: 'Inter',                       value: "Inter, -apple-system, sans-serif" },
  { label: 'Roboto',                      value: "Roboto, 'Helvetica Neue', sans-serif" },
  { label: 'Open Sans',                   value: "'Open Sans', 'Helvetica Neue', sans-serif" },
];

function FontStackField({ label, value, onChange, hint, presets }: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  hint?: string;
  presets: Array<{ label: string; value: string }>;
}) {
  const matchedPreset = presets.find(p => p.value === value);
  const selectValue = matchedPreset ? matchedPreset.value : '__custom__';
  return (
    <div className="tpl-field">
      <label>{label}</label>
      <select
        value={selectValue}
        onChange={(e) => {
          if (e.target.value !== '__custom__') onChange(e.target.value);
          // Picking "Custom" leaves value untouched so the text field below is editable
        }}
        style={{ padding: '5px 8px', fontSize: 12, border: '1px solid var(--border)', borderRadius: 3, background: 'var(--surface)', marginBottom: 4 }}
      >
        {presets.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
        <option value="__custom__">Custom — edit below…</option>
      </select>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{ padding: '5px 8px', fontFamily: 'var(--mono)', fontSize: 11, border: '1px solid var(--border)', borderRadius: 3, background: 'var(--surface)' }}
        placeholder="CSS font-family stack"
      />
      {hint && <span className="tpl-field-hint">{hint}</span>}
    </div>
  );
}

/**
 * NumberWithUnitField — Phase 2.10 quick-win.
 *
 * Wraps a number-stepper input around a CSS length value like "14px",
 * "1.8" (unitless), or "30px". The input shows numeric up/down arrows;
 * the unit suffix is preserved on save. Unitless values (line height)
 * pass `unit=''` and an appropriate step (e.g. 0.1).
 */
export function NumberWithUnitField({ label, value, onChange, hint, unit = 'px', min = 0, max = 999, step = 1, tokenPath }: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  hint?: string;
  unit?: string;
  min?: number;
  max?: number;
  step?: number;
  tokenPath?: string;
}) {
  const numeric = parseFloat(value) || 0;
  const { setFocused } = useContext(TokenFocusContext);
  const announceFocus = () => tokenPath && setFocused(tokenPath);
  const announceBlur  = () => tokenPath && setFocused(null);
  return (
    <div className="tpl-field">
      <label>{label}</label>
      <div className="tpl-field-row">
        <input
          type="number"
          value={numeric}
          min={min} max={max} step={step}
          onFocus={announceFocus}
          onBlur={announceBlur}
          onMouseEnter={announceFocus}
          onMouseLeave={announceBlur}
          onChange={(e) => {
            const n = e.target.value;
            onChange(n === '' ? value : `${n}${unit}`);
          }}
          style={{ width: 90, padding: '5px 8px', fontFamily: 'var(--mono)', fontSize: 11, border: '1px solid var(--border)', borderRadius: 3, background: 'var(--surface)' }}
        />
        {unit && (
          <span style={{
            fontFamily: 'var(--mono)', fontSize: 11,
            color: 'var(--text-muted)',
            background: 'var(--surface-alt)',
            padding: '5px 8px', borderRadius: 3,
            border: '1px solid var(--border)',
          }}>
            {unit}
          </span>
        )}
      </div>
      {hint && <span className="tpl-field-hint">{hint}</span>}
    </div>
  );
}

/**
 * FormHeader — Phase 2.10
 *
 * Standardised header for each form group inside the Template Editor.
 * Renders the group title, an optional intro hint, and (optionally) a
 * "↻ Reset all" button on the right that snaps every token in the group
 * back to its Citadel default. Confirms before resetting.
 */
function FormHeader({ title, hint, onResetAll }: {
  title: string;
  hint?: string;
  onResetAll?: () => void;
}) {
  const [confirmReset, setConfirmReset] = useState(false);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: hint ? 4 : 14 }}>
      <h2 style={{ margin: 0 }}>{title}</h2>
      {onResetAll && (
        <>
          <button
            type="button"
            onClick={() => setConfirmReset(true)}
            className="btn btn-secondary"
            style={{ fontSize: 11, padding: '4px 10px', whiteSpace: 'nowrap', flexShrink: 0 }}
            title={`Snap every ${title} token back to defaults`}
          >
            ↻ Reset all
          </button>
          <ConfirmDestructiveModal
            open={confirmReset}
            danger="archive"
            title={`Reset all ${title} tokens to defaults?`}
            description={`Custom edits in this section will be lost. Edits in other sections (Palette, Typography, etc.) are unaffected.`}
            confirmLabel="Reset section"
            onConfirm={() => { setConfirmReset(false); onResetAll(); }}
            onCancel={() => setConfirmReset(false)}
          />
        </>
      )}
    </div>
  );
}

function ToggleField({ label, value, onChange, hint }: {
  label: string; value: boolean; onChange: (v: boolean) => void; hint?: string;
}) {
  return (
    <div className="tpl-field">
      <label>{label}</label>
      <div className="tpl-field-row">
        <button
          type="button"
          onClick={() => onChange(!value)}
          aria-pressed={value}
          style={{
            width: 36, height: 18, border: 'none', borderRadius: 9,
            background: value ? 'var(--accent)' : 'var(--border)',
            position: 'relative', cursor: 'pointer', transition: 'background 0.15s',
          }}
        >
          <span style={{
            position: 'absolute', top: 2, left: value ? 20 : 2,
            width: 14, height: 14, background: '#fff', borderRadius: '50%',
            transition: 'left 0.15s', display: 'block',
          }} />
        </button>
        <span style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--mono)' }}>
          {value ? 'on' : 'off'}
        </span>
      </div>
      {hint && <span className="tpl-field-hint">{hint}</span>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Per-group form components
// ---------------------------------------------------------------------------

export function PaletteForm({ theme, onChange }: { theme: ThemeJson; onChange: (next: ThemeJson) => void }) {
  const update = (key: keyof ThemeJson['palette']) => (v: string) =>
    onChange({ ...theme, palette: { ...theme.palette, [key]: v } });
  const resetAll = () => onChange({ ...theme, palette: { ...CITADEL_DEFAULTS.palette } });
  return (
    <>
      <FormHeader title="Palette" onResetAll={resetAll} />
      <p className="tpl-form-hint">Every colour in the rendered HTML is derived from these 10 tokens. Severity and HTTP get their own panes.</p>
      <div className="tpl-form-group">
        <h3><span>Surfaces</span></h3>
        <div className="tpl-field-grid">
          <ColourField label="Background"     value={theme.palette.bg}           onChange={update('bg')}           hint="Page / report background" tokenPath="palette.bg" />
          <ColourField label="Surface"        value={theme.palette.surface}      onChange={update('surface')}      hint="Cards, content areas" tokenPath="palette.surface" />
          <ColourField label="Surface alt"    value={theme.palette.surface_alt}  onChange={update('surface_alt')}  hint="Audience-tab bar, alt rows" tokenPath="palette.surface_alt" />
          <ColourField label="Code bg"        value={theme.palette.code_bg}      onChange={update('code_bg')}      hint="Inline code, payload blocks" tokenPath="palette.code_bg" />
        </div>
      </div>
      <div className="tpl-form-group">
        <h3><span>Brand</span></h3>
        <div className="tpl-field-grid">
          <ColourField label="Brand"          value={theme.palette.brand}        onChange={update('brand')}        hint="Cover band, anchors, body text" tokenPath="palette.brand" />
          <ColourField label="Accent"         value={theme.palette.accent}       onChange={update('accent')}       hint="Active tab, links, firm name" tokenPath="palette.accent" />
        </div>
      </div>
      <div className="tpl-form-group">
        <h3><span>Text</span></h3>
        <div className="tpl-field-grid">
          <ColourField label="Text"           value={theme.palette.text}         onChange={update('text')}         hint="All primary prose" tokenPath="palette.text" />
          <ColourField label="Text muted"     value={theme.palette.text_muted}   onChange={update('text_muted')}   hint="Metadata labels, captions" tokenPath="palette.text_muted" />
        </div>
      </div>
      <div className="tpl-form-group">
        <h3><span>Borders</span></h3>
        <div className="tpl-field-grid">
          <ColourField label="Border"         value={theme.palette.border}       onChange={update('border')}       hint="Card borders, hr" tokenPath="palette.border" />
          <ColourField label="Border light"   value={theme.palette.border_light} onChange={update('border_light')} hint="Inner dividers" tokenPath="palette.border_light" />
        </div>
      </div>
    </>
  );
}

export function SeverityForm({ theme, onChange }: { theme: ThemeJson; onChange: (next: ThemeJson) => void }) {
  const updateTier = (tier: keyof ThemeJson['severity']) => (field: 'border'|'text'|'label') => (v: string) =>
    onChange({ ...theme, severity: { ...theme.severity, [tier]: { ...theme.severity[tier], [field]: v } } });
  const toggleTier = (tier: keyof ThemeJson['severity']) => (v: boolean) =>
    onChange({ ...theme, severity: { ...theme.severity, [tier]: { ...theme.severity[tier], enabled: v } } });
  const tiers: Array<keyof ThemeJson['severity']> = ['critical','high','medium','low','info'];
  const resetAll = () => onChange({
    ...theme,
    severity: {
      critical: { ...CITADEL_DEFAULTS.severity.critical },
      high:   { ...CITADEL_DEFAULTS.severity.high },
      medium: { ...CITADEL_DEFAULTS.severity.medium },
      low:    { ...CITADEL_DEFAULTS.severity.low },
      info:   { ...CITADEL_DEFAULTS.severity.info },
    },
  });
  return (
    <>
      <FormHeader title="Severity" onResetAll={resetAll} />
      <p className="tpl-form-hint">Five tiers — Critical is the maximum. Rename a tier (e.g. High → Severe) and it carries through to the report headings. Turn a tier off to drop it from the report and from finding classification when it isn&rsquo;t part of your methodology.</p>
      {tiers.map((tier) => {
        const enabled = theme.severity[tier]?.enabled !== false;
        return (
          <div key={tier} className="tpl-form-group" style={{ borderLeft: `3px solid ${theme.severity[tier].border}`, opacity: enabled ? 1 : 0.55 }}>
            <h3 style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <span>{tier.toUpperCase()}{tier === 'critical' ? ' · new' : ''}</span>
              <ToggleField label={enabled ? 'Enabled' : 'Disabled'} value={enabled} onChange={toggleTier(tier)} />
            </h3>
            <div className="tpl-field-grid">
              <ColourField label="Border"  value={theme.severity[tier].border} onChange={updateTier(tier)('border')} hint="Card left border"/>
              <ColourField label="Text"    value={theme.severity[tier].text}   onChange={updateTier(tier)('text')}   hint="Badge text + filter button"/>
              <TextField   label="Label"   value={theme.severity[tier].label}  onChange={updateTier(tier)('label')}  hint="Tier name in report headings"/>
            </div>
          </div>
        );
      })}
    </>
  );
}

export function TypographyForm({ theme, onChange }: { theme: ThemeJson; onChange: (next: ThemeJson) => void }) {
  const update = (key: keyof ThemeJson['typography']) => (v: string) =>
    onChange({ ...theme, typography: { ...theme.typography, [key]: v } });
  const resetAll = () => onChange({ ...theme, typography: { ...CITADEL_DEFAULTS.typography } });
  return (
    <>
      <FormHeader title="Typography" onResetAll={resetAll} />
      <p className="tpl-form-hint">Font families and sizes. Sizes accept any CSS length (px, em, rem). Change <code>size_body</code> to shrink the entire report uniformly.</p>
      <div className="tpl-form-group">
        <h3><span>Font Stacks</span></h3>
        <div className="tpl-field-grid single">
          <FontStackField label="Serif" value={theme.typography.serif} onChange={update('serif')} presets={SERIF_STACKS} hint="Cover title, finding title, narrative prose, CVSS, tabs" />
          <FontStackField label="Mono"  value={theme.typography.mono}  onChange={update('mono')}  presets={MONO_STACKS}  hint="Firm name, section headers, code, HTTP" />
          <FontStackField label="Sans"  value={theme.typography.sans}  onChange={update('sans')}  presets={SANS_STACKS}  hint="Body text" />
        </div>
      </div>
      <div className="tpl-form-group">
        <h3><span>Sizes</span></h3>
        <div className="tpl-field-grid">
          <NumberWithUnitField label="Body"          value={theme.typography.size_body}         onChange={update('size_body')}         hint="Body prose in finding card · default 14px" min={8} max={28} step={1} tokenPath="typography.size_body" />
          <NumberWithUnitField label="Heading"       value={theme.typography.size_heading}      onChange={update('size_heading')}      hint="Finding title — default 19px" min={12} max={36} step={1} tokenPath="typography.size_heading" />
          <NumberWithUnitField label="Section label" value={theme.typography.size_section}      onChange={update('size_section')}      hint="Section header labels — default 10px" min={8} max={20} step={1} tokenPath="typography.size_section" />
          <NumberWithUnitField label="Meta"          value={theme.typography.size_meta}         onChange={update('size_meta')}         hint="Metadata + audience tabs — default 11px" min={8} max={20} step={1} tokenPath="typography.size_meta" />
          <NumberWithUnitField label="Code"          value={theme.typography.size_code}         onChange={update('size_code')}         hint="Inline code + HTTP block — default 12px" min={8} max={20} step={1} tokenPath="typography.size_code" />
          <NumberWithUnitField label="Cover title"   value={theme.typography.size_cover_title}  onChange={update('size_cover_title')}  hint="Big title on cover — default 30px" min={18} max={56} step={1} tokenPath="typography.size_cover_title" />
          <NumberWithUnitField label="Cover client"  value={theme.typography.size_cover_client} onChange={update('size_cover_client')} hint="Client name on cover — default 26px" min={16} max={48} step={1} tokenPath="typography.size_cover_client" />
          <NumberWithUnitField label="Line height"   value={theme.typography.line_height}       onChange={update('line_height')}       hint="All multi-line text · default 1.8" unit="" min={1} max={3} step={0.05} tokenPath="typography.line_height" />
        </div>
      </div>
    </>
  );
}

/**
 * Catalogue of cover-page layouts. The renderer (Phase 2.11 stage 2) will
 * switch on theme.cover.layout_id and emit the matching markup. For now
 * the editor picks one and stores the choice; the actual report still
 * uses the legacy single-cover layout until renderer support lands.
 *
 * Source: latest/section-prototypes/prototype-104-cover-gallery.html
 */
export const COVER_LAYOUTS: Array<{
  id: string;
  name: string;
  description: string;
  /** True if the renderer has a dedicated implementation. False layouts
   *  fall through to Layout A so the gallery flags them as "coming soon"
   *  and prevents consultants picking a layout that won't actually change
   *  the rendered output. */
  implemented: boolean;
  slots: {
    required: string[];
    optional: string[];
  };
}> = [
  {
    id: 'A',
    name: 'Light Authority',
    description: 'Firm default — top-band firm name + cream body + accent rule. From prototype-01-cover.html.',
    implemented: true,
    // NOTE: required/optional in this slot map describes what the LAYOUT
    // displays, not what the consultant must fill in at template-edit time.
    // The Cover Fields form below is preview-only sample data; per-engagement
    // values are filled in by the Report Builder. So we keep classification /
    // version / status / dates marked "optional" here — the template never
    // forces them; the engagement does.
    slots: {
      required: ['firm_name', 'title', 'client_name'],
      optional: ['firm_logo', 'engagement_ref', 'lead_consultant', 'additional_consultants', 'test_period', 'date', 'classification', 'version'],
    },
  },
  {
    id: 'B',
    name: 'Bold Editorial',
    description: 'Hero client name typography, magazine-cover feel. Smaller firm mark.',
    implemented: true,
    slots: {
      required: ['firm_name', 'client_name', 'title'],
      optional: ['firm_logo', 'date', 'classification', 'version'],
    },
  },
  {
    id: 'C',
    name: 'Modernist Minimalist',
    description: 'Pure white, heavy whitespace, type-driven. Optional small firm logo.',
    implemented: true,
    slots: {
      required: ['firm_name', 'title', 'client_name', 'date'],
      optional: ['firm_logo', 'classification', 'engagement_ref', 'lead_consultant', 'test_period', 'version'],
    },
  },
  {
    id: 'D',
    name: 'Government / Compliance',
    description: 'OFFICIAL banding top and bottom, formal grid metadata. For HMG-style work.',
    implemented: true,
    slots: {
      required: ['firm_name', 'title', 'client_name', 'classification', 'lead_consultant', 'date'],
      optional: ['firm_logo', 'engagement_ref', 'test_period', 'version', 'distribution'],
    },
  },
  {
    id: 'E',
    name: 'Centred Symmetrical',
    description: 'Classic title-page, axis-centred. No top band; type drives the page.',
    implemented: true,
    slots: {
      required: ['firm_name', 'title', 'client_name'],
      optional: ['firm_logo', 'date', 'lead_consultant', 'engagement_ref', 'version', 'classification'],
    },
  },
  {
    id: 'F',
    name: 'Two-tone Split',
    description: 'Vertical split — left half firm identity, right half report info.',
    implemented: true,
    slots: {
      required: ['firm_name', 'title', 'client_name', 'lead_consultant', 'test_period'],
      optional: ['firm_logo', 'firm_tagline', 'date', 'classification', 'engagement_ref', 'version'],
    },
  },
  {
    id: 'G',
    name: 'Accredited Firm',
    description: 'Default base + accreditation logo strip. Unlimited accreditation logos.',
    implemented: true,
    slots: {
      required: ['firm_name', 'title', 'client_name', 'accreditation_logos'],
      optional: ['firm_logo', 'lead_consultant', 'test_period', 'date', 'engagement_ref', 'version', 'classification'],
    },
  },
  {
    id: 'H',
    name: 'Prototype-01 Faithful',
    description: 'Hero client name + dual meta boxes. Matches the original prototype-01 mockup pixel-for-pixel.',
    implemented: true,
    slots: {
      required: ['firm_name', 'title', 'client_name', 'lead_consultant', 'test_period', 'date'],
      optional: ['firm_logo', 'engagement_ref', 'version', 'classification'],
    },
  },
];

/** Format a slot key for display ("firm_logo" → "Firm logo"). */

/**
 * Mini visual diagram of a cover layout. Pure SVG so it scales cleanly
 * inside the gallery cards. Each layout has a recognisably different shape
 * so the consultant can pick by silhouette without reading the description.
 */
function CoverLayoutThumbnail({ id, theme }: { id: string; theme: ThemeJson }) {
  // Merge per-layout overrides so each thumbnail previews its own effective palette.
  const perLayout = ((theme as any).cover_overrides as Record<string, { palette?: Record<string, string> }>)?.[id]?.palette ?? {};
  const pal = { ...theme.palette, ...perLayout };
  const brand = pal.brand;
  const accent = pal.accent;
  const surface = pal.surface;
  const muted = pal.text_muted;
  // Each silhouette uses the live theme colours so picking a different
  // template's colours updates every thumbnail simultaneously.
  return (
    <svg viewBox="0 0 80 100" width="100%" style={{ display: 'block', background: surface, borderRadius: 2 }}>
      {id === 'A' && (<>
        <rect x="0" y="0" width="80" height="14" fill={brand} />
        <rect x="6" y="34" width="40" height="3" fill={accent} />
        <rect x="6" y="42" width="50" height="5" fill={brand} />
        <rect x="6" y="52" width="36" height="4" fill={muted} />
        <rect x="6" y="64" width="22" height="2" fill={muted} />
        <rect x="6" y="70" width="22" height="2" fill={muted} />
        <rect x="0" y="92" width="80" height="8" fill={brand} />
      </>)}
      {id === 'B' && (<>
        <rect x="6" y="10" width="20" height="3" fill={brand} />
        <rect x="6" y="28" width="68" height="14" fill={brand} />
        <rect x="6" y="50" width="50" height="4" fill={accent} />
        <rect x="6" y="60" width="40" height="2" fill={muted} />
        <rect x="6" y="86" width="68" height="1" fill={accent} />
      </>)}
      {id === 'C' && (<>
        <rect x="14" y="20" width="52" height="6" fill={brand} />
        <rect x="14" y="32" width="36" height="2" fill={muted} />
        <rect x="14" y="80" width="20" height="2" fill={muted} />
      </>)}
      {id === 'D' && (<>
        <rect x="0" y="0" width="80" height="6" fill={brand} />
        <rect x="6" y="14" width="40" height="3" fill={accent} />
        <rect x="6" y="22" width="60" height="6" fill={brand} />
        <rect x="6" y="32" width="40" height="3" fill={muted} />
        <rect x="6" y="46" width="68" height="1" fill={muted} />
        <rect x="6" y="52" width="20" height="2" fill={muted} />
        <rect x="32" y="52" width="20" height="2" fill={muted} />
        <rect x="6" y="58" width="20" height="2" fill={muted} />
        <rect x="32" y="58" width="20" height="2" fill={muted} />
        <rect x="0" y="94" width="80" height="6" fill={brand} />
      </>)}
      {id === 'E' && (<>
        <rect x="20" y="30" width="40" height="6" fill={brand} />
        <rect x="28" y="42" width="24" height="3" fill={accent} />
        <rect x="22" y="50" width="36" height="4" fill={brand} />
        <rect x="30" y="78" width="20" height="2" fill={muted} />
      </>)}
      {id === 'F' && (<>
        <rect x="0" y="0" width="38" height="100" fill={brand} />
        <rect x="6" y="20" width="22" height="3" fill="#fff" />
        <rect x="6" y="28" width="18" height="2" fill="#fff" opacity="0.7" />
        <rect x="46" y="20" width="28" height="4" fill={brand} />
        <rect x="46" y="28" width="20" height="2" fill={accent} />
        <rect x="46" y="40" width="24" height="2" fill={muted} />
        <rect x="46" y="46" width="24" height="2" fill={muted} />
      </>)}
      {id === 'G' && (<>
        <rect x="0" y="0" width="80" height="14" fill={brand} />
        <rect x="6" y="34" width="40" height="3" fill={accent} />
        <rect x="6" y="42" width="50" height="5" fill={brand} />
        <rect x="6" y="52" width="36" height="4" fill={muted} />
        {/* Accreditation strip */}
        <rect x="6"  y="70" width="10" height="6" fill={muted} opacity="0.5" />
        <rect x="20" y="70" width="10" height="6" fill={muted} opacity="0.5" />
        <rect x="34" y="70" width="10" height="6" fill={muted} opacity="0.5" />
        <rect x="48" y="70" width="10" height="6" fill={muted} opacity="0.5" />
        <rect x="62" y="70" width="10" height="6" fill={muted} opacity="0.5" />
        <rect x="0" y="92" width="80" height="8" fill={brand} />
      </>)}
      {id === 'H' && (<>
        <rect x="0" y="0" width="80" height="14" fill={brand} />
        <rect x="6" y="22" width="68" height="14" fill={brand} />
        <rect x="6" y="42" width="40" height="3" fill={accent} />
        <rect x="6" y="50" width="36" height="3" fill={muted} />
        {/* Dual meta boxes */}
        <rect x="6"  y="70" width="32" height="20" fill="none" stroke={muted} strokeWidth="0.5" />
        <rect x="42" y="70" width="32" height="20" fill="none" stroke={muted} strokeWidth="0.5" />
      </>)}
    </svg>
  );
}

export function CoverForm({ theme, onChange, samples, onSamplesChange, firmLogo, accreditationLogos, onAccreditationLogosChange, isFirmDefault, onSetAsFirmDefault, canSetAsFirmDefault, onLayoutSelected }: {
  theme: ThemeJson;
  onChange: (next: ThemeJson) => void;
  samples?: PreviewSamples;
  onSamplesChange?: (next: PreviewSamples) => void;
  /** Firm logo data URI (or null). Sourced from firm profile by parent. */
  firmLogo?: string | null;
  firmLogoMime?: string;
  /** Callback to save a new logo or clear it; persists via firm profile. */
  onLogoChange?: (dataUri: string | null, mime: string) => void;
  /** Accreditation / certification logos for Layout G. Firm-wide. */
  accreditationLogos?: AccreditationLogo[];
  onAccreditationLogosChange?: (next: AccreditationLogo[]) => void;
  /** Whether the template currently being edited is the firm-default in DB. */
  isFirmDefault?: boolean;
  /** Wired by TemplateEditorScreen — saves the template (so the latest layout
   *  choice is persisted) AND calls cipherops/templates/set_default to mark it
   *  as the DB firm default. Without this callback the star button falls back
   *  to its legacy localStorage-only behaviour, which doesn't propagate to
   *  Report Builder. */
  onSetAsFirmDefault?: () => Promise<void>;
  /** When false, disables the "Set as firm default" button — typically used
   *  when there's no saved template id yet (new draft) or a save is in flight. */
  canSetAsFirmDefault?: boolean;
  /** Auto-save hook fired when the consultant picks a different layout in the
   *  gallery. Receives the full new theme (with the updated cover.layout_id)
   *  to avoid stale-state issues. Without this callback the layout choice
   *  only lives in React state until the user hits the global Save button —
   *  which surprised consultants who navigated to Report Builder expecting
   *  the new layout to be live. With it, clicks persist immediately. */
  onLayoutSelected?: (nextTheme: ThemeJson) => Promise<void>;
}) {
  // `update()` helper removed — was only used by the now-deleted Cover Tweaks
  // panel (Header band height). If any future cover-form field needs to write
  // back to theme.cover, reintroduce it.
  //
  // Reset All is intentionally comprehensive: resets cover settings,
  // per-layout palette overrides, AND the global palette to Citadel defaults.
  // Global palette changes (e.g. bg set to red during testing) affect every
  // cover layout, so they must be cleared here — not just in the Palette form.
  const resetAll = () => onChange({
    ...theme,
    palette:        { ...CITADEL_DEFAULTS.palette },
    cover:          { ...CITADEL_DEFAULTS.cover },
    cover_overrides: {},
  });
  const activeId = (theme.cover as { layout_id?: string }).layout_id ?? 'A';
  const active = COVER_LAYOUTS.find(l => l.id === activeId) ?? COVER_LAYOUTS[0];
  // Layout selection is a discrete user action and consultants reasonably
  // expect a click to "stick" — so on top of the local state update we fire
  // onLayoutSelected (when wired by parent) to persist immediately. No-op if
  // they clicked the already-active layout.
  const setLayout = (id: string) => {
    if (id === activeId) return;
    const next = { ...theme, cover: { ...theme.cover, layout_id: id } };
    onChange(next);
    if (onLayoutSelected) { void onLayoutSelected(next); }
  };
  const s = samples ?? DEFAULT_PREVIEW_SAMPLES;

  // Firm-wide default cover layout — there are TWO concepts here:
  //
  // 1. **Firm-default template** (DB): `report_templates.is_default = 1`. This
  //    is what `cipherops/templates/get_default` returns to Report Builder, so
  //    it determines the live cover layout for engagements that don't have
  //    their own `template_id`. Toggled by `onSetAsFirmDefault` — saves the
  //    template (so theme.cover.layout_id is persisted) and flips the DB flag.
  // 2. **Cosmetic layout-pick preference** (localStorage): just remembers
  //    "the last layout the consultant marked with the star" so new templates
  //    created from TemplatesScreen can pre-select it. Independent of #1.
  //
  // Pre-fix: the star button only did #2 — which is why it never propagated
  // to Report Builder. Post-fix: it does #1 (via onSetAsFirmDefault) AND #2.
  const FIRM_DEFAULT_LAYOUT_KEY = 'cipherops.firm.default_cover_layout';
  const [firmDefaultLayoutPref, setFirmDefaultLayoutPref] = useState<string>(() => {
    try { return localStorage.getItem(FIRM_DEFAULT_LAYOUT_KEY) ?? 'A'; }
    catch { return 'A'; }
  });
  const [settingDefault, setSettingDefault] = useState(false);
  const handleSetAsFirmDefaultClick = async (id: string) => {
    // Cosmetic localStorage pref — keep it in sync regardless
    try { localStorage.setItem(FIRM_DEFAULT_LAYOUT_KEY, id); }
    catch { /* localStorage may be disabled */ }
    setFirmDefaultLayoutPref(id);
    // Real propagation — only if parent wired the callback
    if (onSetAsFirmDefault) {
      setSettingDefault(true);
      try { await onSetAsFirmDefault(); }
      finally { setSettingDefault(false); }
    }
  };
  // Indicator priority: real DB flag from parent if known, otherwise fall back
  // to the localStorage pref (legacy modal-only callers without the new prop).
  const showAsFirmDefault = isFirmDefault ?? (firmDefaultLayoutPref === active.id);

  return (
    <>
      <FormHeader title="Cover" onResetAll={resetAll} />
      <p className="tpl-form-hint">Pick the cover layout that matches this template's brand. The chosen layout decides which fields the Report Builder asks the consultant to fill in for each engagement.</p>

      <div className="tpl-form-group">
        <h3><span>Cover Layout</span></h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
          {COVER_LAYOUTS.map((layout) => {
            const isActive = activeId === layout.id;
            const isReady = layout.implemented;
            const isFirmDef = (isFirmDefault && layout.id === active.id) || (!isFirmDefault && firmDefaultLayoutPref === layout.id);
            return (
              <button
                key={layout.id}
                type="button"
                disabled={!isReady}
                onClick={() => isReady && setLayout(layout.id)}
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 8,
                  padding: '5px 7px',
                  background: isActive ? '#fef3c7' : 'var(--surface)',
                  border: isActive ? '1px solid var(--accent)' : '0.5px solid var(--border-light)',
                  borderRadius: 4,
                  cursor: isReady ? 'pointer' : 'not-allowed',
                  textAlign: 'left',
                  transition: 'background 0.1s, border-color 0.1s',
                  opacity: isReady ? 1 : 0.5,
                }}
                title={isReady
                  ? `${layout.name} — ${layout.description}`
                  : `${layout.name} — coming soon`}
              >
                <div style={{ flexShrink: 0, width: 44 }}>
                  <CoverLayoutThumbnail id={layout.id} theme={theme} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 1 }}>
                    <span style={{
                      fontFamily: 'var(--mono)', fontSize: 10.5, fontWeight: 700,
                      color: isActive ? 'var(--accent)' : 'var(--text-muted)',
                    }}>{layout.id}</span>
                    {isActive && (
                      <span style={{
                        fontFamily: 'var(--mono)', fontSize: 10, fontWeight: 700,
                        color: 'var(--accent)', background: 'rgba(139,26,46,0.10)',
                        padding: '0px 4px', borderRadius: 9,
                        letterSpacing: '0.08em', textTransform: 'uppercase',
                      }}>✓</span>
                    )}
                    {isFirmDef && (
                      <span style={{
                        fontFamily: 'var(--mono)', fontSize: 10, fontWeight: 700,
                        color: '#1e6635', background: 'rgba(45,122,66,0.12)',
                        padding: '0px 4px', borderRadius: 9,
                        letterSpacing: '0.08em', textTransform: 'uppercase',
                      }}>★</span>
                    )}
                    {!isReady && (
                      <span style={{
                        fontFamily: 'var(--mono)', fontSize: 10, fontWeight: 700,
                        color: 'var(--text-muted)', background: 'var(--surface-alt)',
                        padding: '0px 4px', borderRadius: 9,
                        letterSpacing: '0.08em', textTransform: 'uppercase',
                      }}>soon</span>
                    )}
                  </div>
                  <div style={{
                    fontFamily: 'var(--serif)', fontSize: 11.5,
                    color: isActive ? 'var(--brand)' : 'var(--text)',
                    fontWeight: isActive ? 600 : 400,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>
                    {layout.name}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0 0 14px' }}>
        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
          Active layout for this template: <strong style={{ color: 'var(--brand)' }}>{active.id} — {active.name}</strong>
        </span>
        <span style={{ flex: 1 }} />
        {!showAsFirmDefault && active.implemented && (
          <button
            type="button"
            onClick={() => { void handleSetAsFirmDefaultClick(active.id); }}
            disabled={settingDefault || (onSetAsFirmDefault && canSetAsFirmDefault === false)}
            className="btn btn-secondary"
            style={{ fontSize: 11, padding: '4px 10px' }}
            title={
              onSetAsFirmDefault
                ? `Save this template and make the Report Builder render it for the firm. Engagements that don't override the template will use it.`
                : `Mark Layout ${active.id} as the local preference for new templates (this caller doesn't support DB propagation).`
            }
          >
            {settingDefault ? 'Saving…' : '★ Use this template'}
          </button>
        )}
        {showAsFirmDefault && (
          <span style={{
            fontFamily: 'var(--mono)', fontSize: 10, fontWeight: 700,
            letterSpacing: '0.12em', textTransform: 'uppercase',
            color: '#1e6635',
          }}>★ In use</span>
        )}
      </div>
      <p style={{ margin: '6px 0 14px', fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic' }}>
        ℹ Firm-default applies to engagements with no template assigned. Engagements that already have a template selected keep that choice — pick this template in Report Builder per engagement to switch existing reports.
      </p>

      {/* Per-layout palette overrides — colours that apply only to this layout */}
      {(() => {
        const allOverrides = ((theme as any).cover_overrides as Record<string, { palette?: Record<string, string> }>) ?? {};
        const layoutOverrides = allOverrides[activeId]?.palette ?? {};
        const hasAnyOverride = Object.keys(layoutOverrides).length > 0;

        const updateOverride = (field: string) => (v: string) => {
          const existing = allOverrides[activeId]?.palette ?? {};
          onChange({ ...theme, cover_overrides: { ...allOverrides, [activeId]: { palette: { ...existing, [field]: v } } } } as ThemeJson);
        };

        const clearOverride = (field: string) => {
          const existing = { ...(allOverrides[activeId]?.palette ?? {}) };
          delete existing[field];
          const next = { ...allOverrides };
          if (Object.keys(existing).length === 0) { delete next[activeId]; }
          else { next[activeId] = { palette: existing }; }
          onChange({ ...theme, cover_overrides: next } as ThemeJson);
        };

        const clearAllForLayout = () => {
          const next = { ...allOverrides };
          delete next[activeId];
          onChange({ ...theme, cover_overrides: next } as ThemeJson);
        };

        // Layouts B, C, D, F use `surface` as their full-page background;
        // A, E, G, H use `bg`. Swap the Background / Surface keys + hints
        // so the "Background" picker always controls the actual page background
        // regardless of which layout is active.
        const surfaceIsPageBg = ['B', 'C', 'D', 'F'].includes(activeId);
        const COVER_PAL_FIELDS: Array<{ key: string; label: string; hint: string }> = [
          { key: 'brand',
            label: 'Brand',
            hint: 'Header band, anchors, large title text' },
          { key: 'accent',
            label: 'Accent',
            hint: 'Accent rule, firm name, highlighted elements' },
          { key: surfaceIsPageBg ? 'surface' : 'bg',
            label: 'Background',
            hint: surfaceIsPageBg
              ? 'Full-page background for this layout (uses Surface key)'
              : 'Full-page background for this layout' },
          { key: surfaceIsPageBg ? 'bg' : 'surface',
            label: 'Surface',
            hint: surfaceIsPageBg
              ? 'Outer/report background (less visible for this layout)'
              : 'Content area / card background' },
          { key: 'text',
            label: 'Text',
            hint: 'Body text fallback — bold elements use Brand or Text muted' },
          { key: 'text_muted',
            label: 'Text muted',
            hint: 'Metadata labels, type labels, captions' },
          { key: 'border',
            label: 'Border',
            hint: 'Divider rules, badge outlines' },
          { key: 'border_light',
            label: 'Border light',
            hint: 'Inner dividers (layouts G, H)' },
        ];

        return (
          <div className="tpl-form-group">
            <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span title={active.name}>Colors — Layout {activeId}</span>
              <button
                type="button"
                onClick={clearAllForLayout}
                className="btn btn-secondary"
                style={{ fontSize: 10, padding: '2px 8px', marginLeft: 'auto', opacity: hasAnyOverride ? 1 : 0.45 }}
                title={hasAnyOverride
                  ? `Clear all colour overrides for Layout ${activeId} — reverts to the global palette`
                  : `Layout ${activeId} is already using the global palette`}
                disabled={!hasAnyOverride}
              >
                ↺ Reset Layout {activeId}
              </button>
            </h3>
            <p className="tpl-form-hint">
              Colours set here apply <strong>only to Layout {activeId}</strong>. Each other layout keeps its own settings. Fields with no override use the global palette from the Palette tab.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 12px' }}>
              {COVER_PAL_FIELDS.map(({ key, label, hint }) => {
                const isOverriding = key in layoutOverrides;
                const displayValue = layoutOverrides[key] ?? (theme.palette as Record<string, string>)[key] ?? '#000000';
                const globalValue = (theme.palette as Record<string, string>)[key];
                return (
                  <div key={key} style={{ display: 'flex', alignItems: 'flex-end', gap: 6 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <ColourField
                        label={label}
                        value={displayValue}
                        onChange={updateOverride(key)}
                        hint={isOverriding ? `Overriding global (${globalValue})` : `${hint} — using global`}
                        tokenPath={`cover_overrides.${activeId}.palette.${key}`}
                      />
                    </div>
                    {isOverriding && (
                      <button
                        type="button"
                        onClick={() => clearOverride(key)}
                        title={`Reset to global palette (${globalValue})`}
                        style={{
                          background: 'none',
                          border: '0.5px solid var(--border)',
                          borderRadius: 3,
                          cursor: 'pointer',
                          padding: '4px 8px',
                          fontSize: 11,
                          color: 'var(--text-muted)',
                          height: 28,
                          flexShrink: 0,
                          marginBottom: 14,
                        }}
                      >
                        ×
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {onSamplesChange && s && (() => {
        // All slots that appear in the active layout, grouped by required/optional.
        // Firm logo is handled by its own upload section below; skip it here.
        const allRequired = active.slots.required.filter(k => k !== 'firm_logo' && k !== 'accreditation_logos');
        const allOptional = active.slots.optional.filter(k => k !== 'firm_logo' && k !== 'accreditation_logos');
        // SLOT_FIELDS + REGION_LABELS are now module-level exports
        // (COVER_SLOT_FIELDS / COVER_REGION_LABELS) so the Report Builder
        // can import the same definitions without duplicating them.
        const SLOT_FIELDS = COVER_SLOT_FIELDS;
        const REGION_LABELS = COVER_REGION_LABELS;
        const renderSlotField = (slotKey: string, required: boolean) => {
          const def = SLOT_FIELDS[slotKey];
          if (!def) return null;
          return (
            <div key={slotKey} style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <label style={{
                fontFamily: 'var(--mono)', fontSize: 10, fontWeight: 700,
                letterSpacing: '0.10em', textTransform: 'uppercase',
                color: required ? 'var(--text)' : 'var(--text-muted)',
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
                {def.label}
                {required
                  ? <span style={{ fontSize: 10.5, color: '#c44a1a', background: 'rgba(196,74,26,0.1)', padding: '1px 5px' }}>REQUIRED</span>
                  : <span style={{ fontSize: 10.5, color: 'var(--text-muted)', background: 'var(--surface-alt)', padding: '1px 5px' }}>OPTIONAL</span>
                }
              </label>
              {/* Read-only static display of the sample value. Was an editable
                  <input> previously but the field is preview-only and editing
                  it just confused users who thought they were authoring real
                  content. Render as a styled div with the same visual footprint
                  as a form-input so the layout stays consistent. */}
              <div
                style={{
                  fontFamily: def.mono ? 'var(--mono)' : undefined,
                  fontSize: 11,
                  padding: '6px 9px',
                  background: 'var(--surface-alt)',
                  border: '0.5px solid var(--border-light)',
                  borderRadius: 2,
                  color: 'var(--text-muted)',
                  minHeight: 16,
                  userSelect: 'none',
                }}
              >
                {(s[def.key] as string) || <em style={{ opacity: 0.6 }}>(no sample value)</em>}
              </div>
              {slotKey === 'test_period' && (
                <div
                  style={{
                    fontFamily: 'var(--mono)',
                    fontSize: 11,
                    padding: '6px 9px',
                    background: 'var(--surface-alt)',
                    border: '0.5px solid var(--border-light)',
                    borderRadius: 2,
                    color: 'var(--text-muted)',
                    minHeight: 16,
                    marginTop: 4,
                    userSelect: 'none',
                  }}
                >
                  {(s.test_end as string) || <em style={{ opacity: 0.6 }}>(no sample value)</em>}
                </div>
              )}
              {/* "Appears as ..." hint — tells the consultant exactly where on
                  the rendered cover this field shows up, instead of having to
                  type test values to figure it out. The iframe is sandboxed so
                  we can't outline the actual cover element; this form-side
                  pointer is the next-best UX. */}
              <div style={{ fontSize: 10.5, color: 'var(--text-muted)', fontStyle: 'italic', marginTop: 2 }}>
                → {def.appearsAt}
              </div>
            </div>
          );
        };

        // Group required + optional slots by their cover region so the form
        // mirrors the visual structure of the rendered cover. Within each
        // region, required fields appear above optional ones.
        const regionOrder: CoverFieldRegion[] = ['header', 'title', 'meta', 'footer'];
        const grouped: Record<CoverFieldRegion, Array<{ slot: string; required: boolean }>> = {
          header: [], title: [], meta: [], footer: [],
        };
        allRequired.forEach(slot => {
          const def = SLOT_FIELDS[slot];
          if (def) grouped[def.region].push({ slot, required: true });
        });
        allOptional.forEach(slot => {
          const def = SLOT_FIELDS[slot];
          if (def) grouped[def.region].push({ slot, required: false });
        });

        return (
          <div className="tpl-form-group" style={{ opacity: 0.7 }}>
            <h3 style={{ color: 'var(--text-muted)' }}><span>Live Preview Sample Values — Layout {active.id}</span></h3>
            <div style={{
              padding: '10px 12px', marginBottom: 12,
              background: 'var(--surface-alt)',
              border: '0.5px solid var(--border-light)', borderLeft: '3px solid var(--text-muted)',
              fontSize: 11.5, color: 'var(--text-muted)', lineHeight: 1.6,
            }}>
              <strong>Preview-only — not saved.</strong> These placeholder values feed the live preview on the right so you can see how Layout {active.id} reflows with realistic content. They never persist to the template or any engagement.<br />
              Real per-engagement values (title, dates, version, classification, consultants, etc.) are filled in <strong>per engagement</strong> in the Report Builder.
            </div>
            {regionOrder.map(region => {
              const fields = grouped[region];
              if (fields.length === 0) return null;
              return (
                <div key={region} style={{ marginBottom: 14 }}>
                  <div style={{
                    fontFamily: 'var(--mono)', fontSize: 10.5, fontWeight: 700,
                    letterSpacing: '0.18em', textTransform: 'uppercase',
                    color: 'var(--text-muted)', marginBottom: 8,
                    paddingBottom: 4, borderBottom: '0.5px solid var(--border-light)',
                  }}>
                    {REGION_LABELS[region]}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {fields.map(f => renderSlotField(f.slot, f.required))}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* Firm Logo — read-only preview. The logo is firm-wide (single
          firm_profile.firm_logo_path) and edited in Firm Settings; this
          panel just shows the current logo so the designer can see what
          the cover preview will use. The upload UI used to live here too
          but was removed (single source of truth — Firm Settings). */}
      {(active.slots.required.includes('firm_logo') || active.slots.optional.includes('firm_logo')) && (
        <div className="tpl-form-group">
          <h3><span>Firm Logo</span></h3>
          <p style={{ margin: '0 0 10px', fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic' }}>
            {active.slots.required.includes('firm_logo')
              ? `Required by Layout ${active.id}. The logo appears on the cover band — usually shown white-inverted on the navy strip.`
              : `Optional for Layout ${active.id}. Falls back to a letter-mark of the firm name's first character if no logo is uploaded.`}
            {' '}Logo is firm-wide and edited in Firm Settings.
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            {firmLogo ? (
              <div style={{ background: theme.palette.brand, padding: '12px 18px', borderRadius: 2, display: 'inline-flex', alignItems: 'center', gap: 12 }}>
                <img
                  src={firmLogo}
                  alt="Firm logo preview"
                  style={{ height: 40, width: 'auto', objectFit: 'contain', filter: 'brightness(0) invert(1)' }}
                />
              </div>
            ) : (
              <div style={{ color: 'var(--text-muted)', fontSize: 12, fontStyle: 'italic' }}>
                No logo uploaded — the cover shows a letter-mark fallback.
              </div>
            )}
            <a
              href="#/settings"
              style={{ fontSize: 11.5, color: 'var(--accent)', fontFamily: 'var(--mono)', letterSpacing: '0.04em', textDecoration: 'none' }}
            >
              Edit in Firm Settings →
            </a>
          </div>
        </div>
      )}

      {/* Cover Fields section above (merged preview values into slot fields) */}

      {/* Accreditation logos panel — only shown when active layout has the
          accreditation_logos slot (Layout G). Firm-wide assets (every
          template using Layout G shares the same logo strip), persisted to
          firm_profile.accreditation_logos_json via onAccreditationLogosChange. */}
      {onAccreditationLogosChange && (active.slots.required.includes('accreditation_logos') || active.slots.optional.includes('accreditation_logos')) && (
        <div className="tpl-form-group">
          <h3><span>Accreditation Logos</span></h3>
          <p style={{ margin: '0 0 10px', fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic' }}>
            Logos appear in the strip at the bottom of Layout {active.id}. Reorder with ↑/↓; remove with ✕. Firm-wide — every template using a layout with an accreditation slot shares this list.
          </p>
          {(accreditationLogos ?? []).length === 0 && (
            <div style={{ padding: '10px 12px', background: 'var(--surface-alt)', border: '1px dashed var(--border)', borderRadius: 4, fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>
              No accreditation logos uploaded. The cover will fall back to placeholder text. Upload your firm's certifications below.
            </div>
          )}
          {(accreditationLogos ?? []).map((logo, idx) => (
            <div key={logo.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', border: '0.5px solid var(--border)', background: 'var(--surface)', marginBottom: 6 }}>
              <div style={{ width: 56, height: 40, background: '#fff', border: '0.5px solid var(--border-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <img src={logo.data_uri} alt={logo.title} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
              </div>
              <input
                className="form-input"
                value={logo.title}
                onChange={(e) => {
                  const next = [...(accreditationLogos ?? [])];
                  next[idx] = { ...logo, title: e.target.value };
                  onAccreditationLogosChange(next);
                }}
                placeholder="Accreditation title"
                style={{ flex: 1, fontSize: 12 }}
              />
              <button
                type="button"
                onClick={() => {
                  if (idx === 0) return;
                  const next = [...(accreditationLogos ?? [])];
                  [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
                  next.forEach((l, i) => { l.order = i; });
                  onAccreditationLogosChange(next);
                }}
                disabled={idx === 0}
                style={{ background: 'none', border: '1px solid var(--border)', padding: '2px 8px', cursor: idx === 0 ? 'not-allowed' : 'pointer', opacity: idx === 0 ? 0.4 : 1 }}
                title="Move up"
              >↑</button>
              <button
                type="button"
                onClick={() => {
                  const len = (accreditationLogos ?? []).length;
                  if (idx >= len - 1) return;
                  const next = [...(accreditationLogos ?? [])];
                  [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
                  next.forEach((l, i) => { l.order = i; });
                  onAccreditationLogosChange(next);
                }}
                disabled={idx >= (accreditationLogos ?? []).length - 1}
                style={{ background: 'none', border: '1px solid var(--border)', padding: '2px 8px', cursor: 'pointer', opacity: idx >= (accreditationLogos ?? []).length - 1 ? 0.4 : 1 }}
                title="Move down"
              >↓</button>
              <button
                type="button"
                onClick={() => {
                  const next = (accreditationLogos ?? []).filter((_, i) => i !== idx);
                  next.forEach((l, i) => { l.order = i; });
                  onAccreditationLogosChange(next);
                }}
                style={{ background: 'none', border: '1px solid var(--border)', color: '#c44a1a', padding: '2px 8px', cursor: 'pointer' }}
                title="Remove"
                aria-label="Remove accreditation logo"
              >✕</button>
            </div>
          ))}
          <label className="btn btn-secondary" style={{ cursor: 'pointer', fontSize: 12, padding: '6px 12px', display: 'inline-block', marginTop: 6 }}>
            + Add accreditation logo
            <input
              type="file"
              accept="image/png,image/jpeg,image/svg+xml"
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                const reader = new FileReader();
                reader.onload = () => {
                  const dataUri = reader.result as string;
                  const mime = (file.type === 'image/svg+xml' || file.type === 'image/jpeg' || file.type === 'image/png') ? file.type : 'image/png';
                  const list = accreditationLogos ?? [];
                  const newLogo: AccreditationLogo = {
                    id: 'accred_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8),
                    title: file.name.replace(/\.[^.]+$/, ''),
                    data_uri: dataUri,
                    mime: mime as AccreditationLogo['mime'],
                    order: list.length,
                  };
                  onAccreditationLogosChange([...list, newLogo]);
                  // Clear the input so the same file can be re-selected
                  (e.target as HTMLInputElement).value = '';
                };
                reader.readAsDataURL(file);
              }}
            />
          </label>
          <span className="tpl-field-hint" style={{ marginTop: 8, display: 'block' }}>
            PNG, JPEG, or SVG · max ~500 KB each · 5 logos per row recommended
          </span>
        </div>
      )}

      {/* "Cover Tweaks" panel removed entirely.
          Its only field (Header band height) wrote to theme.cover.header_height,
          exposed as the CSS variable --cover-header-h. That variable is consumed
          only by `.cover-band` in renderer.ts — and no cover layout (A through H)
          renders an element with that class. Each layout has its own hardcoded
          top-band CSS (.cover-a-top-band, .cover-h .top-band, etc.) for design
          fidelity to prototype-104. So the slider was dead config.
          Same precedent as the "Footer band text" removal: drop the editor,
          leave the underlying theme field in place for backwards compatibility
          with older saved templates. */}
    </>
  );
}

export function FindingsForm({ theme, onChange }: { theme: ThemeJson; onChange: (next: ThemeJson) => void }) {
  const update = <K extends keyof ThemeJson['findings']>(key: K) => (v: ThemeJson['findings'][K]) =>
    onChange({ ...theme, findings: { ...theme.findings, [key]: v } });
  const resetAll = () => onChange({ ...theme, findings: { ...CITADEL_DEFAULTS.findings } });
  return (
    <>
      <FormHeader title="Findings" onResetAll={resetAll} />
      <p className="tpl-form-hint">Finding card layout — severity border, card radius, and CVSS visibility.</p>
      <div className="tpl-form-group">
        <h3><span>Card</span></h3>
        <div className="tpl-field-grid">
          <NumberWithUnitField label="Severity border width" value={theme.findings.severity_border_width} onChange={update('severity_border_width')} hint="Coloured left border on finding cards · 0 = no border · default 3px" min={0} max={12} step={1} tokenPath="findings.severity_border_width" />
          <NumberWithUnitField label="Card radius"           value={theme.findings.card_radius}           onChange={update('card_radius')}           hint="Corner roundness · 0 = sharp (default)" min={0} max={24} step={1} tokenPath="findings.card_radius" />
          <ToggleField label="Show CVSS score"     value={theme.findings.show_cvss}             onChange={update('show_cvss')} />
        </div>
      </div>
    </>
  );
}

export function AppendixForm({ theme, onChange }: { theme: ThemeJson; onChange: (next: ThemeJson) => void }) {
  const update = (key: keyof ThemeJson['appendix']) => (v: boolean) =>
    onChange({ ...theme, appendix: { ...theme.appendix, [key]: v } });
  const resetAll = () => onChange({ ...theme, appendix: { ...CITADEL_DEFAULTS.appendix } });
  return (
    <>
      <FormHeader title="Appendix" onResetAll={resetAll} />
      <p className="tpl-form-hint">Toggle which appendix sub-sections render. Engagement-specific.</p>
      <div className="tpl-form-group">
        <h3><span>Subsections</span></h3>
        <div className="tpl-field-grid">
          <ToggleField label="Show Scope"        value={theme.appendix.show_scope}       onChange={update('show_scope')} />
          <ToggleField label="Show Methodology"  value={theme.appendix.show_methodology} onChange={update('show_methodology')} />
          <ToggleField label="Show Tools"        value={theme.appendix.show_tools}       onChange={update('show_tools')} />
          <ToggleField label="Show Tester Bio"   value={theme.appendix.show_tester_bio}  onChange={update('show_tester_bio')} />
        </div>
      </div>
    </>
  );
}

export function HttpForm({ theme, onChange }: { theme: ThemeJson; onChange: (next: ThemeJson) => void }) {
  const update = (key: keyof ThemeJson['http']) => (v: any) =>
    onChange({ ...theme, http: { ...theme.http, [key]: v } });
  const resetAll = () => onChange({ ...theme, http: { ...CITADEL_DEFAULTS.http } });
  return (
    <>
      <FormHeader title="HTTP Blocks" onResetAll={resetAll} />
      <p className="tpl-form-hint">Styling for HTTP request/response evidence (Burp output, raw captures).</p>
      <div className="tpl-form-group">
        <h3><span>Surface</span></h3>
        <div className="tpl-field-grid">
          <ColourField label="Block bg"     value={theme.http.bg}           onChange={update('bg')} />
          <ColourField label="Default text" value={theme.http.text}         onChange={update('text')} />
          <ColourField label="Header name"  value={theme.http.header_name}  onChange={update('header_name')} />
          <ColourField label="Header value" value={theme.http.header_value} onChange={update('header_value')} />
        </div>
      </div>
      <div className="tpl-form-group">
        <h3><span>Methods</span></h3>
        <div className="tpl-field-grid">
          <ColourField label="GET"     value={theme.http.method_get}     onChange={update('method_get')} />
          <ColourField label="POST"    value={theme.http.method_post}    onChange={update('method_post')} />
          <ColourField label="PUT"     value={theme.http.method_put}     onChange={update('method_put')} />
          <ColourField label="DELETE"  value={theme.http.method_delete}  onChange={update('method_delete')} />
          <ColourField label="OPTIONS" value={theme.http.method_options} onChange={update('method_options')} />
        </div>
      </div>
      <div className="tpl-form-group">
        <h3><span>Status codes</span></h3>
        <div className="tpl-field-grid">
          <ColourField label="2xx" value={theme.http.status_2xx} onChange={update('status_2xx')} />
          <ColourField label="3xx" value={theme.http.status_3xx} onChange={update('status_3xx')} />
          <ColourField label="4xx" value={theme.http.status_4xx} onChange={update('status_4xx')} />
          <ColourField label="5xx" value={theme.http.status_5xx} onChange={update('status_5xx')} />
        </div>
      </div>
      <div className="tpl-form-group">
        <h3><span>URL & payload</span></h3>
        <div className="tpl-field-grid">
          <ColourField label="URL path"          value={theme.http.url_path}          onChange={update('url_path')} />
          <ColourField label="URL param"         value={theme.http.url_param}         onChange={update('url_param')} />
          <ColourField label="Payload highlight" value={theme.http.payload_highlight} onChange={update('payload_highlight')} />
        </div>
      </div>
      <div className="tpl-form-group">
        <h3><span>Layout</span></h3>
        <div className="tpl-field-grid">
          <TextField   label="Font size"        value={theme.http.font_size}      onChange={update('font_size')}      hint="Default 11px" mono />
          <ToggleField label="Show line numbers" value={theme.http.show_line_numbers} onChange={update('show_line_numbers')} />
          <ColourField label="Line num colour"   value={theme.http.line_num_color}    onChange={update('line_num_color')} />
        </div>
      </div>
    </>
  );
}


// ---------------------------------------------------------------------------
// Live preview pane (mini cover band + finding card)
// ---------------------------------------------------------------------------

export type PreviewView = 'cover' | 'finding' | 'http';
export type SeverityTier = 'critical' | 'high' | 'medium' | 'low' | 'info';

/**
 * Preview sample values — Phase 2.11 stage 2b.
 *
 * The Template Editor's live preview renders against placeholder data so
 * the consultant sees a realistic cover/finding/HTTP block while editing
 * theme tokens. These are NOT persisted to the template; they only drive
 * the in-editor preview. Setting them here lets the consultant audit
 * how the layout looks with real-feeling data instead of the static
 * "Acme Corporation" defaults.
 */
/**
 * Cover slot regions — used to group fields visually in both the
 * Template Editor's preview-values form and the Report Builder's
 * per-engagement cover form. Same grouping in both places so the UX
 * pattern is consistent.
 */
export type CoverFieldRegion = 'header' | 'title' | 'meta' | 'footer';

/**
 * Cover field metadata: label, hint, region, "appears as ..." description.
 * Keyed by the slot id used in COVER_LAYOUTS (firm_name, client_name,
 * title, classification, lead_consultant, date, engagement_ref,
 * test_period, version, firm_tagline, status, distribution).
 *
 * The `key` field maps to the local PreviewSamples shape used by the
 * Template Editor's preview iframe. The Report Builder binds the same
 * slot ids to engagement columns + report_overrides_json instead, so
 * it ignores `key` and uses the slot id directly.
 */
export interface CoverSlotFieldDef {
  /** PreviewSamples key — used by Template Editor preview only. Report
   *  Builder binds slot ids to engagement / overrides directly. */
  key: keyof PreviewSamples;
  label: string;
  hint: string;
  appearsAt: string;
  region: CoverFieldRegion;
  mono?: boolean;
}

export const COVER_SLOT_FIELDS: Record<string, CoverSlotFieldDef> = {
  firm_name:       { key: 'firm_name',      label: 'Firm Name',            hint: 'Your firm / company name',                                  region: 'header', appearsAt: 'Top-left of cover header band, monospace caps' },
  client_name:     { key: 'client_name',    label: 'Client Name',          hint: 'Client company or organisation',                            region: 'title',  appearsAt: 'Hero serif text on the cover (largest typography)' },
  title:           { key: 'title',          label: 'Report Title',         hint: 'e.g. Web Application Penetration Test',                     region: 'title',  appearsAt: 'Cover title — separate from the engagement identifier; falls back to engagement.title if blank' },
  classification:  { key: 'classification', label: 'Classification',       hint: 'Confidential · Restricted · Official Sensitive',            region: 'meta',   appearsAt: 'CLS badge near reference + bottom band on most layouts' },
  lead_consultant: { key: 'consultant',     label: 'Lead Consultant',      hint: 'Primary consultant name on the cover',                      region: 'meta',   appearsAt: '"Lead consultant" row on the meta block / "Prepared By" card' },
  additional_consultants: { key: 'consultant', label: 'Additional Consultants', hint: 'Other consultants — one per line. Use "Role: Name" if you want a role prefix (e.g. "Principal: Jane Smith").', region: 'meta', appearsAt: 'Appended below "Lead consultant" on layouts that show a meta block' },
  date:            { key: 'date',           label: 'Report Date',          hint: 'ISO yyyy-mm-dd',                                            region: 'meta',   appearsAt: 'Issue date in the version strip',                        mono: true },
  engagement_ref:  { key: 'engagement_ref', label: 'Engagement Reference', hint: 'e.g. COP-2026-001 — leave blank to auto-generate',          region: 'meta',   appearsAt: 'REF badge above the title block (auto-generated if blank)' },
  test_period:     { key: 'test_start',     label: 'Test Period Start',    hint: 'ISO yyyy-mm-dd — start of testing window',                  region: 'meta',   appearsAt: 'Test Period row on Prepared By / footer grid (start–end)', mono: true },
  version:         { key: 'version',        label: 'Report Version',       hint: 'e.g. 1.0 · 2.0 · DRAFT',                                    region: 'meta',   appearsAt: 'Version strip "Version: <value>"' },
  firm_tagline:    { key: 'firm_tagline',   label: 'Firm Tagline',         hint: 'Strapline shown below firm name on some layouts',           region: 'header', appearsAt: 'Italic strapline under firm name (Layouts F, others)' },
  // status slot removed — cover no longer auto-appends "— <Status>" to the
  // version. Consultants encode status directly into Report Version
  // (e.g. "1.0 — Draft" or "0.5-rc1") for full control.
  distribution:    { key: 'distribution',   label: 'Distribution',         hint: 'e.g. Authorised Recipients Only — used by Layout D',        region: 'meta',   appearsAt: 'Distribution row on the Layout D ref-grid' },
};

export const COVER_REGION_LABELS: Record<CoverFieldRegion, string> = {
  header: 'Header band',
  title:  'Title block',
  meta:   'Reference & meta',
  footer: 'Footer band',
};

/**
 * Accreditation / certification logo. Stored on firm_profile (firm-wide,
 * not per-template) and rendered by Layout G's bottom strip.
 */
export interface AccreditationLogo {
  id: string;
  title: string;
  data_uri: string;
  mime: 'image/png' | 'image/svg+xml' | 'image/jpeg';
  order: number;
}

export interface PreviewSamples {
  // Required across all layouts
  firm_name: string;
  client_name: string;
  title: string;
  classification: string;
  // Common across most layouts
  consultant: string;       // → lead_consultant slot
  date: string;             // → report_date / date slot
  // Optional / layout-specific
  engagement_ref: string;   // custom engagement reference (e.g. COP-2026-001)
  test_start: string;       // start of testing window (ISO date)
  test_end: string;         // end of testing window (ISO date)
  firm_tagline: string;     // firm strapline shown on some layouts
  version: string;          // report version string (e.g. 1.0)
  distribution: string;     // distribution / recipient label (Layout D)
}

// Default sample values for the live preview iframe. These match the
// hardcoded demo strings in latest/section-prototypes/prototype-104-cover-gallery.html
// EXACTLY so consultants can hold the prototype HTML next to the Template
// Editor and see a 1:1 visual match for any layout. NOT persisted to the
// template — these only drive the in-editor preview. Real per-engagement
// values are filled in at Report Builder time.
export const DEFAULT_PREVIEW_SAMPLES: PreviewSamples = {
  // Dummy preview values only — these never persist to the template or any
  // engagement; real values are filled in per-engagement in Report Builder.
  // Avoiding the working product codename in dummy text on purpose.
  firm_name: 'Pentest Reports Done Correctly',
  client_name: 'ACME Corporation',
  title: 'Pentest Reports',
  classification: 'Confidential',
  consultant: 'John Doe',
  date: '2026-05-04',
  engagement_ref: 'WAPT-2026-0108',
  test_start: '2026-01-08',
  test_end: '2026-02-02',
  firm_tagline: 'Precision. Clarity. Assurance.',
  version: '1.0',
  distribution: 'Authorised Recipients Only',
};


/**
 * PreviewPane — Phase 2.10
 *
 * Renders one of three views (cover / finding / http) using the live theme
 * tokens so consultants see immediate feedback as they edit. The active view
 * is controlled by the parent (TemplateEditorScreen has a tab strip in its
 * right-pane header). For the modal/legacy callers, view defaults to
 * undefined, which renders all three views stacked (compatibility mode).
 *
 * For the finding view, severityTier picks which tier's tokens drive the
 * card border + badge — the parent shows sub-tabs (High/Medium/Low/Info)
 * when the consultant is on the Severity rail group so each tier's colours
 * can be inspected.
 */
export function PreviewPane({ theme, name, view, severityTier = 'medium', samples, firmLogo }: {
  theme: ThemeJson;
  name: string;
  view?: PreviewView;
  severityTier?: SeverityTier;
  samples?: PreviewSamples;
  firmLogo?: string | null;
}) {
  const { focused } = useContext(TokenFocusContext);
  const fs = (...t: string[]) => focusStyle(focused, ...t);
  const tier = theme.severity[severityTier];
  // Fall back to the template name when no explicit firm-name sample given —
  // useful when the modal-style legacy caller doesn't supply a samples obj.
  const fallbackSamples: PreviewSamples = {
    ...DEFAULT_PREVIEW_SAMPLES,
    firm_name: (samples?.firm_name && samples.firm_name.trim()) || name || DEFAULT_PREVIEW_SAMPLES.firm_name,
  };
  const s: PreviewSamples = samples ?? fallbackSamples;
  const previewVars: React.CSSProperties = {
    // @ts-expect-error custom CSS variables
    '--p-bg':           theme.palette.bg,
    '--p-surface':      theme.palette.surface,
    '--p-surface-alt':  theme.palette.surface_alt,
    '--p-brand':        theme.palette.brand,
    '--p-accent':       theme.palette.accent,
    '--p-text':         theme.palette.text,
    '--p-text-muted':   theme.palette.text_muted,
    '--p-border':       theme.palette.border,
    '--p-code-bg':      theme.palette.code_bg,
    '--p-sev-border':   tier.border,
    '--p-sev-text':     tier.text,
    '--p-serif':        theme.typography.serif,
    '--p-mono':         theme.typography.mono,
    '--p-cover-title':  theme.typography.size_cover_title,
  };
  const sevWidth = theme.findings.severity_border_width;
  const cardRadius = theme.findings.card_radius;

  const showCover   = !view || view === 'cover';
  const showFinding = !view || view === 'finding';
  const showHttp    = view === 'http';

  // ── Cover preview HTML (IPC-rendered, debounced) ─────────────────────────
  // Calls cipherops/templates/preview_cover with the in-flight theme + samples
  // + firm logo whenever any of those change. Renderer runs in the Electron
  // main process so what we display is exactly what the export will emit —
  // no browser-side renderer bundling.
  const [coverHtml, setCoverHtml] = useState<string>('');
  const [coverError, setCoverError] = useState<string>('');
  useEffect(() => {
    if (!showCover) return;
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const res = await invoke<{ html: string }>('cipherops/templates/preview_cover', {
          theme,
          samples: s,
          firmLogo: firmLogo ?? null,
        });
        if (!cancelled) {
          setCoverHtml(res.html);
          setCoverError('');
        }
      } catch (err) {
        if (!cancelled) {
          setCoverError(err instanceof IpcError ? err.message : String(err));
        }
      }
    }, 200);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [showCover, theme, s, firmLogo]);

  // When the cover view is the only one showing (parent is on the Cover
  // rail group), drop the .tpl-preview wrapper entirely. That wrapper is
  // designed for the legacy stacked preview (cover + finding card + http
  // demo) where each subview needs its own panel chrome. For the cover-only
  // case we want full-bleed rendering like Report Builder uses, no padding
  // or background bezel between the editor canvas and the cover paper.
  const coverOnly = view === 'cover';
  if (coverOnly) {
    return (
      <div style={{ ...previewVars, height: '100%', width: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {coverError ? (
          <div style={{ padding: '40px 28px', color: '#c44a1a', fontFamily: 'system-ui', fontSize: 13, background: '#fef2f2', border: '1px solid #fca5a5' }}>
            <div style={{ fontFamily: 'monospace', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 8 }}>Cover preview error</div>
            <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 11 }}>{coverError}</pre>
          </div>
        ) : !coverHtml ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontFamily: 'var(--serif, Georgia)', fontStyle: 'italic', fontSize: 13, background: '#ffffff' }}>
            Rendering cover preview…
          </div>
        ) : (
          <iframe
            title="Cover Preview"
            srcDoc={coverHtml}
            style={{ flex: 1, width: '100%', border: 'none', background: '#ffffff', display: 'block' }}
            sandbox="allow-same-origin"
          />
        )}
      </div>
    );
  }

  return (
    <div className="tpl-preview" style={previewVars}>
      {!view && (
        <div className="tpl-preview-header">
          <span className="tpl-label-tag">Preview</span>
          <span className="tpl-pill">live</span>
        </div>
      )}

      {showCover && (
        // Full-size WYSIWYG cover preview — IPC-rendered via the same
        // renderer code path as the actual report export. What you see
        // here is exactly what the exported PDF/HTML will produce.
        coverError ? (
          <div style={{
            padding: '40px 28px',
            color: '#c44a1a',
            fontFamily: 'system-ui',
            fontSize: 13,
            background: '#fef2f2',
            border: '1px solid #fca5a5',
            margin: 0,
            minHeight: 600,
          }}>
            <div style={{ fontFamily: 'monospace', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 8 }}>
              Cover preview error
            </div>
            <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 11 }}>
              {coverError}
            </pre>
          </div>
        ) : !coverHtml ? (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            minHeight: 600, color: 'var(--text-muted)', fontFamily: 'var(--serif, Georgia)',
            fontStyle: 'italic', fontSize: 13,
          }}>
            Rendering cover preview…
          </div>
        ) : (
          <iframe
            title="Cover Preview"
            srcDoc={coverHtml}
            style={{
              width: '100%',
              // Fill the available pane height. Min 600px so it always feels
              // page-like even on small screens; max bound by the container.
              height: '100%',
              minHeight: 600,
              border: 'none',
              background: '#ffffff',
              display: 'block',
              marginBottom: showFinding && !view ? 12 : 0,
            }}
            sandbox="allow-same-origin"
          />
        )
      )}

      {showFinding && (
        <div style={{
          background: 'var(--p-surface)',
          borderLeft: `${sevWidth} solid var(--p-sev-border)`,
          padding: '12px 14px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
          borderRadius: cardRadius,
          border: `1px solid var(--p-border)`,
          borderLeftWidth: sevWidth,
          ...fs('palette.surface', 'findings.severity_border_width', 'findings.card_radius', `severity.${severityTier}`),
        }}>
          <div style={{ fontFamily: 'var(--p-mono)', fontSize: theme.typography.size_section, color: 'var(--p-text-muted)', letterSpacing: '0.1em', ...fs('typography.size_section', 'palette.text_muted', 'typography.mono') }}>
            FIND_001
          </div>
          <div style={{ fontFamily: 'var(--p-serif)', fontSize: theme.typography.size_heading, color: 'var(--p-brand)', margin: '2px 0 4px', lineHeight: 1.25, ...fs('typography.size_heading', 'typography.serif', 'palette.brand') }}>
            SQL Injection in Login Form
          </div>
          <span style={{
            display: 'inline-block', fontSize: 10, fontWeight: 700,
            letterSpacing: '0.14em', textTransform: 'uppercase',
            color: 'var(--p-sev-text)',
            border: `1px solid var(--p-sev-border)`,
            borderRadius: 2, padding: '1px 5px', marginBottom: 6,
            ...fs(`severity.${severityTier}`),
            opacity: tier.enabled === false ? 0.4 : 1,
            textDecoration: tier.enabled === false ? 'line-through' : 'none',
          }}>
            {tier.label}
          </span>
          {tier.enabled === false && (
            <span style={{ marginLeft: 8, fontSize: 9.5, fontFamily: 'var(--p-mono)', color: 'var(--p-text-muted)', fontStyle: 'italic' }}>
              tier off — hidden from report
            </span>
          )}
          {theme.findings.show_cvss && (
            <span style={{
              float: 'right', textAlign: 'right',
              borderLeft: '0.5px solid var(--p-border)', paddingLeft: 10, marginLeft: 10,
            }}>
              <div style={{ fontFamily: 'var(--p-serif)', fontSize: 22, color: 'var(--p-text)', ...fs('typography.serif') }}>7.5</div>
              <div style={{ fontFamily: 'var(--p-mono)', fontSize: theme.typography.size_meta, color: 'var(--p-text-muted)', ...fs('typography.size_meta', 'typography.mono', 'palette.text_muted') }}>CVSS:3.1</div>
            </span>
          )}
          <p style={{ fontSize: theme.typography.size_body, lineHeight: theme.typography.line_height, margin: '0 0 0', clear: 'both', ...fs('typography.size_body', 'typography.line_height', 'palette.text') }}>
            The login endpoint at <code style={{ fontFamily: 'var(--p-mono)', fontSize: theme.typography.size_code, background: 'var(--p-code-bg)', padding: '1px 4px', borderRadius: 2, ...fs('typography.size_code', 'typography.mono', 'palette.code_bg') }}>/api/login</code> concatenates user input directly into the SQL query.
          </p>
        </div>
      )}

      {showHttp && (
        <div style={{
          background: theme.http.bg,
          color: theme.http.text,
          fontFamily: theme.typography.mono,
          fontSize: theme.http.font_size,
          padding: '14px 16px',
          borderRadius: cardRadius,
          lineHeight: 1.55,
          overflowX: 'auto',
        }}>
          <div style={{ marginBottom: 8 }}>
            <span style={{ color: theme.http.method_post, fontWeight: 700 }}>POST</span>{' '}
            <span style={{ color: theme.http.url_path }}>/api/login</span>
            <span style={{ color: theme.http.url_param }}>?next=/dashboard</span>{' '}
            HTTP/1.1
          </div>
          <div><span style={{ color: theme.http.header_name }}>Host:</span> <span style={{ color: theme.http.header_value }}>app.target.com</span></div>
          <div><span style={{ color: theme.http.header_name }}>Content-Type:</span> <span style={{ color: theme.http.header_value }}>application/json</span></div>
          <div><span style={{ color: theme.http.header_name }}>Authorization:</span> <span style={{ color: theme.http.header_value }}>Bearer eyJhbGc…</span></div>
          <div style={{ marginTop: 6 }}>
            {`{"username":"admin","password":"`}<span style={{ background: theme.http.payload_highlight, color: '#1a1f2e', padding: '0 2px' }}>{`' OR '1'='1`}</span>{`"}`}
          </div>

          <div style={{ marginTop: 14, paddingTop: 10, borderTop: `1px solid ${theme.palette.border_light}` }}>
            <div style={{ marginBottom: 6 }}>
              HTTP/1.1 <span style={{ color: theme.http.status_4xx, fontWeight: 700 }}>401 Unauthorized</span>
            </div>
            <div><span style={{ color: theme.http.header_name }}>Content-Type:</span> <span style={{ color: theme.http.header_value }}>application/json</span></div>
            <div style={{ marginTop: 6 }}>
              {`{"error":"Authentication failed"}`}
            </div>
          </div>

          <div style={{ marginTop: 14, paddingTop: 10, borderTop: `1px solid ${theme.palette.border_light}`, opacity: 0.85 }}>
            <span style={{ color: theme.http.method_get, fontWeight: 700 }}>GET</span>
            {' '}
            <span style={{ color: theme.http.method_post, fontWeight: 700 }}>POST</span>
            {' '}
            <span style={{ color: theme.http.method_put, fontWeight: 700 }}>PUT</span>
            {' '}
            <span style={{ color: theme.http.method_delete, fontWeight: 700 }}>DELETE</span>
            {' '}
            <span style={{ color: theme.http.method_options, fontWeight: 700 }}>OPTIONS</span>
            {' · '}
            <span style={{ color: theme.http.status_2xx }}>200</span>
            {' '}
            <span style={{ color: theme.http.status_3xx }}>302</span>
            {' '}
            <span style={{ color: theme.http.status_4xx }}>401</span>
            {' '}
            <span style={{ color: theme.http.status_5xx }}>500</span>
          </div>
        </div>
      )}

      {!view && (
        <div style={{ marginTop: 12, fontSize: 10, color: 'var(--text-muted)', fontFamily: 'var(--mono)', textAlign: 'center' }}>
          Re-skins instantly · cover band + finding card
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main modal
// ---------------------------------------------------------------------------

interface Template {
  id: string;
  firm_id: string;
  name: string;
  description: string | null;
  theme_json: string;
  section_visibility_json: string;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

export interface TemplateEditorModalProps {
  template: Template;
  onClose: () => void;
  onSaved: (updated: Template) => void;
}

export default function TemplateEditorModal({ template, onClose, onSaved }: TemplateEditorModalProps) {
  // Hydrate theme from template_json, falling back to Citadel for any missing keys
  const initialTheme = useMemo<ThemeJson>(() => {
    try {
      const parsed = parseJsonField<Partial<ThemeJson>>(template.theme_json, {});
      return {
        palette:         { ...CITADEL_DEFAULTS.palette,    ...(parsed.palette    ?? {}) },
        severity:        { ...CITADEL_DEFAULTS.severity,   ...(parsed.severity   ?? {}) },
        typography:      { ...CITADEL_DEFAULTS.typography, ...(parsed.typography ?? {}) },
        cover:           { ...CITADEL_DEFAULTS.cover,      ...(parsed.cover      ?? {}) },
        cover_overrides: (parsed.cover_overrides ?? {}) as ThemeJson['cover_overrides'],
        findings:        { ...CITADEL_DEFAULTS.findings,   ...(parsed.findings   ?? {}) },
        appendix:        { ...CITADEL_DEFAULTS.appendix,   ...(parsed.appendix   ?? {}) },
        http:            { ...CITADEL_DEFAULTS.http,       ...(parsed.http       ?? {}) },
      };
    } catch {
      return CITADEL_DEFAULTS;
    }
  }, [template.theme_json]);

  const [theme, setTheme] = useState<ThemeJson>(initialTheme);
  const [name, setName] = useState(template.name);
  const [activeGroup, setActiveGroup] = useState<RailGroup['id']>('palette');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const onThemeChange = useCallback((next: ThemeJson) => {
    setTheme(next);
  }, []);

  const handleSave = useCallback(async () => {
    setSaving(true); setError('');
    try {
      const res = await invoke<{ template: Template }>('cipherops/templates/update', {
        template_id: template.id,
        name: name.trim() || template.name,
        theme_json: JSON.stringify(theme, null, 2),
      });
      onSaved(res.template);
      onClose();
    } catch (ex) {
      setError(ex instanceof IpcError ? ex.message : 'Save failed');
    } finally { setSaving(false); }
  }, [template, name, theme, onSaved, onClose]);

  // window.prompt() is blocked in Electron's sandboxed renderer — instead we
  // create the new template with "<current name> (Copy)" and let the consultant
  // rename it inline once the editor reopens with the new copy selected.
  const handleSaveAsNew = useCallback(async () => {
    setSaving(true); setError('');
    try {
      const res = await invoke<{ template: Template }>('cipherops/templates/create', {
        firm_id: template.firm_id,
        name: `${name.trim() || template.name} (Copy)`,
        description: template.description,
        theme_json: JSON.stringify(theme, null, 2),
        section_visibility_json: template.section_visibility_json,
        is_default: false,
      });
      onSaved(res.template);
      onClose();
    } catch (ex) {
      setError(ex instanceof IpcError ? ex.message : 'Save-as-new failed');
    } finally { setSaving(false); }
  }, [template, name, theme, onSaved, onClose]);

  const railSections: Array<{ name: string; items: RailGroup[] }> = [
    { name: 'Visual',   items: RAIL_GROUPS.filter(g => g.section === 'Visual') },
    { name: 'Sections', items: RAIL_GROUPS.filter(g => g.section === 'Sections') },
  ];

  // ── Render the active form
  let form: React.ReactNode = null;
  switch (activeGroup) {
    case 'palette':    form = <PaletteForm    theme={theme} onChange={onThemeChange} />; break;
    case 'severity':   form = <SeverityForm   theme={theme} onChange={onThemeChange} />; break;
    case 'typography': form = <TypographyForm theme={theme} onChange={onThemeChange} />; break;
    case 'cover':      form = <CoverForm      theme={theme} onChange={onThemeChange} />; break;
    case 'findings':   form = <FindingsForm   theme={theme} onChange={onThemeChange} />; break;
    case 'appendix':   form = <AppendixForm   theme={theme} onChange={onThemeChange} />; break;
    case 'http':       form = <HttpForm       theme={theme} onChange={onThemeChange} />; break;
  }

  return (
    <div className="tpl-modal-overlay" onClick={onClose}>
      <div className="tpl-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="tpl-modal-header">
          <span className="tpl-label-tag">Template</span>
          <input
            className="tpl-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Template name"
          />
          {template.is_default && <span className="tpl-label-tag" style={{ opacity: 0.6 }}>·  Default for firm</span>}
          <span style={{ flex: 1 }} />
          {error && <span style={{ color: 'var(--accent)', fontSize: 11, fontFamily: 'var(--mono)' }}>{error}</span>}
          <button className="btn btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-secondary" onClick={handleSaveAsNew} disabled={saving}>Save as new…</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>

        {/* Body */}
        <div className="tpl-modal-body">
          {/* Rail */}
          <aside className="tpl-rail">
            {railSections.map(sec => (
              <div key={sec.name}>
                <div className="tpl-rail-section">{sec.name}</div>
                {sec.items.map(g => (
                  <div
                    key={g.id}
                    className={`tpl-rail-item${activeGroup === g.id ? ' active' : ''}`}
                    onClick={() => setActiveGroup(g.id)}
                  >
                    <span className="tpl-rail-icon">{g.icon}</span>
                    <span>{g.label}</span>
                    {g.count > 0 && <span className="tpl-rail-badge">{g.count}</span>}
                  </div>
                ))}
              </div>
            ))}
          </aside>

          {/* Form */}
          <div className="tpl-form-pane">
            {form}
          </div>

          {/* Preview */}
          <PreviewPane theme={theme} name={name} />
        </div>
      </div>
    </div>
  );
}
